import { sharedStringsEn } from "../shared";

export const mobileStringsEn = {
  /**
   *
   * Contractor Mobile App
   *
   */
  ui: {
    back: "Back",
    confirm: "Confirm",
    continue: "Continue",
    required: "Required",
    add_more: "Add more",
    offline_indicator: {
      offline: "Offline",
      pending_upload_singular: "1 upload pending",
      pending_upload_plural: "{count} uploads pending",
    },
    error: {
      something_went_wrong: "Something went wrong",
      error_msg: "The app encountered an error. Our team has been notified.",
      refresh: "Refresh",
    },
  },
  auth: {
    error_messages: {
      ...sharedStringsEn.auth.error_messages,
      // Overrides:
      form_identifier_not_found: "Account not found. Reach out to your office admin.",
      form_identifier_exists: "That email address is already in use",
      verification_failed: "Verification failed.",
      too_many_requests: "Too many failed attempts.",
    },
    // These codes describe an email everywhere except the phone form, so they cannot live in error_messages.
    phone_errors: {
      form_conditional_param_missing: "Invalid phone number",
      form_param_format_invalid: "Invalid phone number",
      form_identifier_exists: "Phone number already in use",
      verification_not_sent: "Verification text not sent",
    },
    actions: {
      log_in: "Log in",
      back: "Back",
      log_out: "Log out",
    },
    alerts: {
      code_sent: "We've sent a code to",
      code_resent: "We've sent another code to",
    },
    prompts: {
      phone_number: "Phone number",
      verification_code: "Enter verification code",
      did_not_get_text: "Didn't get a text?",
      send_again: "Send again",
      not_a_contractor: "Not a Remi contractor?",
      become_one_here: "Become one here",
    },
    agent_sign_in: {
      signing_in: "Signing in…",
      failed: "Couldn't sign in with this link. Mint a new one and try again.",
    },
    logout_modal: {
      title: "Log out?",
      description: "You'll be signed out and will need to enter your login info to get back in.",
      cancel_button: "No thanks",
      confirm_button: "Log out",
      loading_text: "Signing out...",
      error_message: "Failed to sign out. Please try again.",
    },
    delete_account_modal: {
      title: "Delete account?",
      description:
        "This will erase all personal data and preferences associated with your account in accordance with our privacy policy. This action is irreversible.",
      cancel_button: "No thanks",
      confirm_button: "Delete",
      loading_text: "Deleting...",
      error_message: "Failed to delete account. Please try again.",
    },
    setup_name: {
      welcome: "Welcome to Remi",
      description: "Please enter your name to get started. This helps us identify your work on the job site.",
      first_name: "First Name",
      first_name_placeholder: "Enter your first name",
      last_name: "Last Name",
      last_name_placeholder: "Enter your last name",
      required_alert: "Please enter both your first and last name.",
      error_alert: "Failed to save your name. Please try again.",
      saving: "Saving...",
    },
    link_access: {
      verifying: "Verifying access...",
      redirecting: "Redirecting...",
      title: "Unable to access link",
      invalid_link: "Invalid link",
      generic_error: "Failed to access link",
      permission_denied: "Notifications must be enabled to open this link. Turn them on in Settings, then try again.",
      push_token_failed: "We couldn't verify this device. Check your connection and try again.",
      contact_supervisor: "Please contact your supervisor if this problem persists.",
      retry: "Try again",
    },
    email_label: "Email",
    email_placeholder: "name@email.com",
    password_label: "Password",
    password_placeholder: "Enter password",
    use_phone_instead: "Use phone number instead",
    forgot_password: "Forgot password?",
    credential: {
      forgot_password_title: "Reset password",
      forgot_password_prompt: "Enter your email to receive a reset code",
      reset_code_prompt: "Enter the code sent to your email",
      new_password_label: "New password",
      new_password_placeholder: "Enter new password",
      reset_password_button: "Reset password",
      setup_description: "Add an email and password to secure your account for multi-factor authentication.",
      add_email_label: "Email address",
      add_email_placeholder: "name@email.com",
      verify_email_prompt: "Enter the verification code sent to your email",
      create_password_label: "Create a password",
      create_password_placeholder: "Enter a password",
      setup_complete_title: "Email and password added",
      password_set: "Password set",
      setup_button: "Continue",
      verify_button: "Verify",
      save_password_button: "Save password",
      use_different_email: "Use a different email",
      password_requirements_title: "Your password must:",
      password_requirement_length: "Be at least {count} characters",
      password_requirement_common: "Not be a common word or predictable pattern",
      password_requirement_breach: "Not appear in a known data breach",
      enrollment_prompt_title: "Set up email and password now",
      enrollment_prompt_description: "Please add an email and password to your account",
      enrollment_prompt_button: "Create login",
      forgot_password_sent: "If an account exists for that email, we've sent a reset code.",
    },
    mfa_challenge: {
      title: "Two-step verification",
      totp_subtitle: "Enter the code from your authenticator app",
      sms_subtitle: "Enter the code sent to {phone}",
      backup_subtitle: "Enter one of your backup codes",
      method_totp: "Authenticator app",
      method_sms: "Text message",
      method_backup: "Backup code",
      use_different_method: "Use a different method",
      verify_button: "Verify",
      resend_code: "Resend code",
      code_resent: "Code resent",
      resend_failed: "We couldn't send a code. Check your signal and try again.",
      same_number_info: "This code is different from the one you used to sign in",
      back_to_signin: "Back to sign in",
      invalid_code: "Invalid code. Please try again.",
    },
    mfa_required: {
      title: "Two-step verification required",
      enrolled_description:
        "Your session needs to be verified. Sign out and sign back in to complete two-step verification.",
      unenrolled_description: "Your account requires two-step verification. Set it up on the web to continue.",
      sign_out_button: "Sign out",
      setup_web_button: "Set up on web",
    },
  },
  onboarding: {
    shared: {
      skip: "Skip",
      open_settings: "Open settings",
    },
    terms_of_service: {
      subtitle: "Reason for camera and photo access",
      title: "Remi's privacy transparency",
      body: "To document project progress and verify work completion, we ask that you allow access to your camera and photo library.\n\nWe only access photos you choose to capture or upload through the app. We do not access your camera or photo library beyond that, and we do not use your photos for facial recognition or identification purposes.",
      terms_of_service: "Terms of service",
      privacy_policy: "Privacy policy",
      learn_more: "Learn more",
      agree_to_terms: "By using Remi, you agree to our {termsOfService} and {privacyPolicy}",
      terms_acknowledgement: "I've read and agree to the terms of service and privacy policy",
      terms_acknowledgement_error: "You must agree to the terms of service to continue",
      agree_and_continue: "Agree and continue",
    },
    turn_on_camera: {
      title: "You're almost there — just one more step",
      description: "Make your job easier, allow camera access, this way you can capture photos directly to Remi.",
      grant_camera_access: "Grant camera access",
    },
    turn_on_notifications: {
      title: "Get notified when you're checked in",
      description: "To save you time and make your job easier, you can tap the alert to view the project details.",
      turn_on_notifications: "Yes, notify me",
      notifications_already_enabled: "Notifications are already enabled! You're all set.",
      enable_in_settings: "Please enable notifications in your device settings",
      notifications_enabled: "Notifications are already enabled! You're all set.",
    },
    walkthrough: {
      project_checklists: "Project checklists",
      checklists_msg: "Each project has requirements you'll need to complete. Tap any item to see what's needed.",
      requirement_instructions: "Requirement instructions",
      instructions_msg:
        "Each requirement item has instructions, this includes a photo example and detailed explanation of what's needed.",
      completing_checklist: "Completing a checklist",
      completing_msg:
        "Once a checklist is finished, click submit to notify your Remi admin that it's ready for review.",
    },
  },
  home: {
    checked_in_jobs: "Checked in jobs",
    day_picker: {
      one_job: "1 job",
      jobs: "{count} jobs",
      see_all: "See all",
      previous_month: "Previous month",
      next_month: "Next month",
      today: "today",
    },
    empty_state: {
      title: "No jobs to show — yet!",
      description: "Once you arrive at a job site, your job will appear here automatically.",
    },
    header: {
      open_schedule: "Open schedule",
    },
    no_jobs_for_day: "No jobs scheduled for this day.",
    route: {
      next: "Next",
      next_stop: "Next · {window}",
      directions: "Directions",
      details: "Details",
      schedule: "Schedule",
      stops_summary_singular: "1 stop · {done} done",
      stops_summary_plural: "{count} stops · {done} done",
      today_label: "Today · {date}",
      offline_local: "Offline · Local",
      no_stops_today: "No stops today",
      no_stops_on_day: "No stops this day",
      no_stops_offline: "You're not on the route. Reconnect to see new stops from dispatch.",
      no_stops_online: "You're not on the route. Open your schedule to see what's next.",
      all_done_title: "All stops done",
      all_done_description: "Every stop on this day is finished.",
      stop_count_singular: "1 stop",
      stop_count_plural: "{count} stops",
      next_day_tomorrow: "Tomorrow · {stops}",
      next_day_dated: "{date} · {stops}",
      nothing_upcoming: "Nothing upcoming",
      open_in_google_maps_title: "Open in “Google Maps”?",
      open_in_google_maps_cancel: "Cancel",
      open_in_google_maps_open: "Open",
      review_needed: "Review needed · {item}",
      today: "Today",
      this_week: "This week",
      distance_miles: "{miles} mi",
      done_chip: "Done",
      agenda_subtitle: "{category} · {city}, {state}",
      show_today: "Show today's stops",
      hide_today: "Back to next stop",
      load_failed_title: "Couldn't load your stops",
      load_failed_description: "Check your connection and try again.",
      retry: "Try again",
      window_short: {
        AM: "AM",
        PM: "PM",
        EVENING: "EVE",
        ANY: "Any",
      },
    },
    tabs: {
      home: "Home",
      profile: "Profile",
    },
    schedule: {
      search_placeholder: "Search jobs, addresses, claims",
      week: "Week",
      month: "Month",
      today_header: "Today · {date}",
      day_header: "{weekday} · {date}",
      no_stops: "No stops",
      no_results: "No jobs match “{query}”",
    },
    menu: {
      checklist: "Checklist",
      terms_and_conditions: "Terms and conditions",
      privacy_notice: "Privacy notice",
      prohibited_use_policy: "Prohibited use policy",
      version: {
        line: "Version {version} ({build})",
        line_with_environment: "Version {version} ({build}) · {environment}",
        unknown: "unknown",
      },
    },
  },
  profile_redesign: {
    credential_title: "Add email & password",
    credential_description: "Phone-only sign-in · set up to use any device",
    credential_action: "Set up",
    organization: "Organization",
    admin_role: "Admin",
    preferences: "Preferences",
    notifications: "Notifications",
    notifications_managed_ios: "Managed in iOS Settings",
    notifications_managed_android: "Managed in system settings",
    on: "On",
    off: "Off",
    save_images: "Save images to phone",
    save_images_description: "Copy camera shots to your photo library",
    location: "Location access",
    admin_impersonate: "Manage impersonation",
    admin_stop_impersonating: "Stop impersonating {name}",
    admin_stopping: "Stopping…",
    admin_clear_onboarding: "Clear onboarding",
    admin_clear_link_auth: "Clear link auth",
    admin_invalidate_queries: "Invalidate all queries",
    admin_test_logger: "Test mobile logger",
    admin_seed_job: "Seed site visit job",
    admin_seed_job_with_checklist: "Seed site visit job with filled checklist",
    admin_seeding: "Seeding…",
    admin_push_token: "Push token",
    impersonation_title: "Impersonate a user",
    impersonation_close: "Close",
    impersonation_search: "Search by name, email, or organization",
    impersonation_hint: "Contractor accounts that can sign in",
    impersonation_error: "Couldn’t load users. Try again.",
    impersonation_empty: "No matching users",
    impersonation_select: "Select a user",
    impersonation_start: "Impersonate {name}",
    admin_section: "Admin",
    legal: "Legal",
    footer: "{version} · Signed in as {identity}",
  },
  job_detail_redesign: {
    done: "Done",
    subtitle: "{category} · {window} · {city}",
    subtitle_without_window: "{category} · {city}",
    message: "Message {name}",
    call: "Call {name}",
    copy: "Copy {field}",
    copied: "Copied",
    rows: {
      mobile: "Mobile",
      email: "Email",
      roof: "Roof",
      claim: "Claim",
      carrier: "Carrier",
      scheduled: "Scheduled",
      scope_notes: "Scope notes",
      site_notes: "Site notes",
      checked_in: "Checked in",
      checklist_completed: "Checklist done",
    },
    roof_squares: "{count} sq",
    roof_age: "{years} yr",
    roof_material: {
      ASPHALT: "comp shingle",
      ROLLED_COMP: "rolled comp",
      METAL: "metal",
      TILE: "tile",
      TPO: "TPO",
      EPDM: "EPDM",
      SHAKE: "shake",
    },
    checklist_title: "Inspection checklist",
    structure_title: "Structure {number}",
    checklist_progress: "{completed} of {total} questions · {status}",
    checklist_not_started: "not started",
    start_inspection: "Start inspection",
    continue_inspection: "Continue inspection",
    edit_inspection: "Edit inspection",
    on_the_way_title: "Heading here next?",
    work_order_title: "{category} work order",
    work_order_subtitle: "The work this QA visit is checking",
  },
  checklist_redesign: {
    back_to_job: "Job",
    optional: "Optional",
    photos_required: "Required · {n} of {required} photos",
    photos_optional: "Optional · {n} of {max} photos",
    photos_required_over: "Required · {n} photos",
    photos_optional_count_one: "Optional · 1 photo",
    photos_optional_count_other: "Optional · {n} photos",
    pick_one_required: "Required · pick one",
    yes: "Yes",
    no: "No",
    next_section: "Next · {section}",
    submit_inspection: "Submit inspection",
    submit_qa: "Submit QA",
    open_sections: "Open sections",
    edit_checklist: "Edit checklist",
    confirm_value: "Confirm {value}",
    use_this_date: "Use this date",
    date_today: "Today",
    date_confirmed: "Confirmed",
    date_offset_one: "{sign}1 day",
    date_offset_other: "{sign}{n} days",
    decrease: "Decrease",
    increase: "Increase",
    previous_day: "Previous day",
    next_day: "Next day",
    type_or_dictate: "Type or dictate",
    dictate: "Dictate",
    save_note: "Save note",
    update_note: "Update note",
    take_photo: "Take photo",
    take_photo_number: "Take photo {n} of {required}",
    add_another_photo: "Add another",
    take_video: "Take video",
    take_another_video: "Take another video",
    upload_from_library: "Library",
    open_photo: "Open photo {n}",
    open_video: "Open video {n}",
    open_all_photos: "Open all {n} photos",
    photo_grid_count_one: "1 photo",
    photo_grid_count_other: "{n} photos",
    photo_grid_uploading: "{count} · {n} uploading",
    photo_grid_hint: "Newest first · tap to view · hold to remove",
    photo_grid_done: "Done",
    photo_uploading: "Uploading",
    remove_photo_title: "Remove this photo?",
    remove_photo_confirm: "Remove",
    remove_photo_cancel: "Cancel",
    remove_photo_below_required: "This item needs {required} photos. Removing this one leaves {n}.",
    viewer_close: "Close",
    viewer_counter: "{n} of {total}",
    viewer_download: "Save to Photos",
    viewer_remove: "Remove photo",
    group_done: "Done",
    group_progress: "{done} of {total}",
    submit: "Submit",
    sections_label: "Sections",
    sheet_eyebrow: "{address} · {kind}",
    sheet_kind_inspection: "Inspection",
    sheet_kind_qa: "QA visit",
    submit_title_inspection: "Submit inspection?",
    submit_title_qa: "Submit QA?",
    submit_title_open_one: "Submit with 1 open item?",
    submit_title_open_other: "Submit with {n} open items?",
    submit_subtitle: "{done} of {total} answered · the office gets the report now",
    submit_incomplete_subtitle: "The office sees what's missing and can send it back for the rest.",
    submit_as_incomplete: "Submit as incomplete",
    review_needed: "Review needed",
    review_card_one: "Retake 1 item · {section}",
    review_card_other: "Retake {n} items · {section}",
    retake_requested: "Retake requested",
    retake_photo: "Retake photo",
    retake_video: "Retake video",
    structures_title: "Structures",
    structures_intro: "Every insured structure gets the full inspection. Tap one to work on it.",
    structure_complete: "Complete",
    structure_in_progress: "In progress",
    structure_not_started_one: "Not started · 1 question",
    structure_not_started_other: "Not started · {total} questions",
    structure_resume: "Resume",
    add_structure: "+ Add a structure",
    from_the_claim: "From the claim",
    from_the_claim_meta: "From the claim · confirm or edit",
  },
  upload_activity: {
    uploading_title: "Uploading photos",
    waiting_title: "Waiting to upload",
    synced_title: "Photos synced",
    progress: "{done} of {total} photos",
    waiting: "Waiting · {n} saved",
    synced: "Synced",
  },
  project: {
    loading: "Loading...",
    not_found: "Project not found",
    not_found_msg: "The project you're looking for doesn't exist or you don't have access to it.",
    view_all_projects: "View All Projects",
    checked_in: "Checked in",
    scope_of_work_description: "Scope of work description",
    scope_of_work: "Scope of work",
    ho_info: "HO info",
    homeowner_info: "Homeowner info",
    phone_withheld: "Phone number withheld",
    key_dates: "Key dates",
    details: "Details",
    files: "Files",
    photos: "Photos",
    tabs: {
      overview: "Overview",
      checklist: "Checklist",
      photos: "Photos",
    },
    financials: "Financials",
    my_tasks: "My tasks",
    all: "All",
    current: "Current",
    completed: "Completed",
    general: "General",
    project_properties: "Project properties",
    project_tasks: "Project tasks",
    copy_project_link: "Copy project link",
    generating_link: "Generating link...",
    checklist_complete: "Checklist complete",
    unable_edit_checklist: "Unable to edit checklist",
    edit_checklist_unavailable_offline:
      "This checklist isn't saved on this device yet. Connect to the internet to edit it.",
    edit_checklist_sync_failed:
      "A checklist you edited offline couldn't be reopened, likely because the office already reviewed it. Your changes are kept on this device. Tell the office.",
    opening: "Opening\u2026",
    edit: "Edit",
    continue_checklist: "Continue photo checklist",
    begin_checklist: "Begin photo checklist",
    copied: "Copied",
    link_copied: "Project link copied to clipboard",
    link_error: "Failed to generate project link",
    record_install_started: "Record install started",
    create_upload_work_order: "Create and upload work order",
    get_ahj_approval: "Get approval from AHJ",
    change_order_status: "Change order approved/rejected by EPC",
    validate_photos: "Validate work completed photos are acceptable",
    eagleview: {
      title: "EagleView",
      description: "Overview of the roof's layout and measurements",
    },
    work_order: {
      title: "{category} work order",
      description: "The work this QA visit is checking",
      unavailable: "Couldn't open the work order. Please try again.",
    },
    roof_image: {
      title: "Roof image",
      description: "Clear visual guidance on roof area",
      unavailable: "Roof image unavailable",
    },
    job_details: {
      title: "Job details",
      job_type: "Job type",
      square_count: "Square count",
      square_count_singular: "square",
      square_count_plural: "squares",
      stop: "Stop",
      site_visit_window: {
        AM: "Morning (7–12)",
        PM: "Afternoon (12–5)",
        EVENING: "Evening (5–9)",
        ANY: "Flexible",
      },
      permit_required: "Permit required",
      final_inspection_required: "Final inspection required",
      ahj_requirements: "AHJ requirements",
      hover_link: "View in Hover",
      sow_notes: "SOW notes",
      unattended_visit: "Unattended visit",
      checklist_completed: "Checklist completed at",
    },
    additional_information: {
      title: "Additional information from the homeowner",
    },
    job_context: {
      button: "Job context",
      title: "Job context",
      job_type: "Job type",
      homeowner_title: "Homeowner",
      escalation_label: "Escalation",
      escalation_active: "Open escalation",
      escalation_previously: "Previously escalated",
      escalation_none: "No escalations",
      risk_label: "Relationship risk",
      risk_scored_at: "Relationship risk · scored {date}",
      risk_unscored: "Not scored yet",
      risk_tiers: {
        HEALTHY: "Healthy",
        WATCH: "Watch",
        AT_RISK: "At risk",
        CRITICAL: "Critical",
      },
      work_title: "Work done at this home",
      work_completed: "Completed {date}",
      work_contractor_unknown: "Contractor not on file",
      work_empty: "No completed work on file for this home.",
      load_failed: "Couldn't load the job context. Check your connection and try again.",
      retry: "Try again",
    },
    escalation: {
      active_title: "Escalated homeowner",
      active_body: "This homeowner has an open escalation. Be prepared before you knock.",
      previously_title: "Previously escalated homeowner",
      previously_body: "This homeowner was escalated in the past. Approach with care.",
    },
    on_the_way: {
      button: "I'm on the way",
      confirm_title: "Heading to this job?",
      confirm_message: "We'll text the homeowner that you're on the way.",
      cancel: "Cancel",
      confirm: "Send",
      notified: "Homeowner notified you're on the way",
      error: "Couldn't let the homeowner know. Please try again.",
      another_en_route: "Finish your current visit before heading to the next one.",
    },
    cant_make_it: {
      button: "Can't make it",
      confirm_title: "Can't make this visit?",
      confirm_message:
        "We'll take it off your route and find the homeowner a new time. If they're already expecting you, we'll text them about the change.",
      cancel: "Keep it",
      confirm: "I can't make it",
      done_title: "Removed from your route",
      done_rebooked: "We've booked the homeowner a new time.",
      done_unbooked: "Dispatch will find the homeowner a new time.",
      ok: "OK",
      error: "Couldn't remove this visit. Please try again.",
    },
    check_in: {
      title: "Check-in",
      checked_in_message: "You've been checked in for this job:",
      checked_in_time: "Checked in at",
      undo_check_in: "Undo check in",
      not_on_site: "Not on site?",
      view_project: "View project",
      undo_check_in_modal: {
        title: "Not on site?",
        description:
          "It looks like you might've been checked in by mistake. Undoing this will remove your check-in status.",
        cancel: "Cancel",
        confirm: "Undo check in",
        error: "Failed to undo check-in. Please try again.",
      },
      multiple_worksites_title: "Looks like you're near more than one job. Select the job site you are currently on:",
      safety_acknowledgement_text:
        "I acknowledge that myself and all others on site have access to emergency information, restrooms and understand the hazardous areas of the worksite",
    },
    checklist: {
      another_visit_active: "Finish your current visit before starting this one.",
      not_found: "Checklist not found",
      not_found_msg: "This checklist doesn't exist or you don't have access to it.",
      submit: "Submit",
      submit_incomplete_title: "Submit incomplete checklist?",
      submit_incomplete_message:
        "Some required items are still missing. Once you submit, you won't be able to add more answers.",
      submit_incomplete_confirm: "Submit",
      submit_incomplete_cancel: "Cancel",
      submit_empty_title: "Cannot submit empty checklist",
      submit_empty_message: "Answer at least one question before submitting.",
      syncing: "Syncing…",
      submitting: "Submitting…",
      complete_title: "Checklist complete",
      approved_title: "Checklist approved",
      submitted_title: "Checklist submitted!",
      submitted_description: "Great - Thank you for completing the checklist, your Remi admin has been notified.",
      back_to_project: "Back to project",
      back_to_home: "Back to home",
      site_walk: "Site walk",
      photo_house_number: "Photo of house number",
      photo_required: "{n} photo required",
      n_required: "{n} required",
      signature: "Signature",
      signature_required: "{n} signature required",
      sign: "Sign",
      signature_field: "Signature field",
      selfie_house_number: "Selfie with house number",
      selfie_instructions:
        "Take selfie of yourself in front of the house number for evidence you are beginning work on the correct home.",
      ladder_location: "Ladder location",
      ladder_location_photo: "Ladder location photo",
      preexisting_property_damage: "Images of pre-existing property damage",
      preexisting_issues_property: "Images of pre-existing issues on the property",
      preexisting_equipment: "Is there any pre-existing issues with the equipment?",
      preexisting_property: "Is there any pre-existing issues on the property?",
      noticeable_damage: "Is there any noticeable damage that occurred previous to you arriving to the property?",
      yes: "Yes",
      no: "No",
      photos_required: "{n}/{total} photos required",
      photos_optional: "Optional",
      photos_uploading: "Uploading…",
      photo_waiting_to_upload: "Photo waiting to upload",
      photos_waiting_to_upload: "{count} photos waiting to upload",
      no_photos_uploaded: "No photos uploaded",
      step_progress: "{completed} of {total}",
      items_uploading: "{n} items uploading...",
      offline_waiting: "Device offline. {n} items waiting to upload.",
      offline_waiting_alt: "You're offline. {n} items waiting to upload.",
      upload_incomplete_title: "Upload incomplete",
      upload_incomplete_some_photos:
        "Some photos were no longer available and could not be saved. The remaining photos were saved.",
      upload_incomplete_no_photos:
        "The selected photo was no longer available and could not be saved. Please try again.",
      upload_failed_title: "Upload failed",
      upload_failed_message: "Could not upload. Please try again.",
      upload_failed_icloud:
        "This item is stored in iCloud and couldn't be downloaded to your phone. Check your connection and try again.",
      videos_skipped_title: "Some videos were not added",
      videos_skipped_too_long: "Videos must be {seconds} seconds or shorter. Trim the video and try again.",
      videos_skipped_too_large: "Videos must be {megabytes} MB or smaller. Choose a shorter clip and try again.",
      videos_skipped_unsupported: "Only MP4, MOV, WebM and 3GP videos can be added.",
      conditional_questions: "Conditional questions",
      enter_response: "Enter your response",
      enter_a_number: "Enter a number",
      select_a_date: "Select a date",
      upload_file: "Upload file",
      tap_to_upload: "Tap to upload",
      loading: "Loading…",
      yes_no: "Yes / No",
      select: "Select",
      select_one: "Select one",
      repeat_item_fallback: "Item",
      jump_to_section: "Jump to section",
      optional: "Optional",
      unavailable: "Checklist unavailable.",
      offline_not_loaded_title: "Not available offline",
      offline_not_loaded_msg:
        "This checklist hasn't been loaded on this device yet. Connect to the internet to open it, then it will work offline.",
      progress: "Progress",
      changed_by_another_user: "Changed by another user",
      field_locked_by: "{name} is editing this field",
      field_locked_by_unknown: "Another user is editing this field",
      field_uploading_by: "{name} is currently uploading…",
      field_uploading_by_unknown: "Someone is currently uploading…",
      visit_checklists: "Visit checklists",
      visit_unavailable_msg: "This visit's checklists couldn't be loaded, or you don't have access to them.",
      visit_progress: "{submitted} of {total} submitted",
      visit_complete: "Inspection submitted",
      visit_in_progress: "{count} in progress",
      open_checklists: "Open",
      status_not_started: "Not started",
      status_draft: "In progress",
      status_submitted: "Submitted",
      status_approved: "Approved",
      status_rejected: "Review needed",
      row_canceled: "Removed from this visit",
      uploads_failed: "{count} failed to upload",
      change_checklist_type: "Change",
      change_checklist_type_title: "Which checklist is this?",
      change_checklist_type_failed_title: "Checklist not changed",
      change_checklist_type_failed_message:
        "Someone may have started this checklist already. Pull to refresh and check which one it is.",
      change_checklist_type_submitted_message: "This checklist was already submitted, so its type can't be changed.",
      change_checklist_type_unpublished_message:
        "That checklist isn't available for this job yet. Pick another one, or tell the office.",
      change_checklist_type_unavailable_offline_message:
        "This checklist type isn't saved on this device yet. Connect to the internet to change it.",
      change_checklist_type_sync_failed_message:
        "A checklist type you changed offline couldn't be saved, so that checklist kept its old type. Tell the office.",
      change_checklist_type_offline_message: "Couldn't reach Remi. Check your connection and try again.",
      change_checklist_type_started_message: "You've already started this checklist, so its type can't be changed.",
      add_structure: "Add another structure",
      add_structure_confirm_title: "Add another structure?",
      add_structure_confirm_message:
        "Adds another {checklist} checklist for a different building at this property, like a detached garage.",
      add_structure_confirm: "Add",
      add_structure_cancel: "Cancel",
      add_structure_failed_title: "Structure not added",
      add_structure_failed_message: "Pull to refresh and try again. If it keeps failing, tell the office.",
      add_structure_unavailable_offline_message:
        "This checklist isn't saved on this device yet. Connect to the internet to add a structure.",
      add_structure_sync_failed_message:
        "A structure you added offline couldn't be saved. Its checklist is kept on this device. Tell the office.",
      structure_label: "Structure {number}",
      add_structure_submitted_message:
        "Every checklist on this visit has been submitted, so a structure can't be added.",
      switch_checklist: "Switch checklist",
      submit_visit: "Submit inspection",
      verdict_required_title: "Verdict needed",
      verdict_required_message: "Answer whether this work needs corrections before you submit.",
      verdict_required_for: "{checklists} still needs a verdict before you can submit.",
      visit_submitted_title: "Inspection submitted",
      visit_submitted_message: "Every checklist in this visit has been submitted.",
      visit_submit_partial_title: "Some checklists didn't submit",
      visit_submit_partial_message: "{checklists} didn't submit. Open each one from the list and submit it again.",
      visit_submit_needs_connection_title: "Connect to submit",
      visit_submit_needs_connection_message:
        "This phone doesn't have {checklists} saved yet. Connect to the internet to submit the inspection.",
    },
    downloadPhotos: {
      buttonLabel: "Download checklist photos",
      savingPhotos: "Saving photos...",
      successTitle: "Downloaded",
      successMessage: "{count} photos saved to your camera roll.",
      successMessageSingular: "1 photo saved to your camera roll.",
      partialTitle: "Download complete",
      partialMessage: "Saved {success} of {total} photos. {failed} failed to download.",
      failedTitle: "Download failed",
      failedMessage: "Could not save photos. Please try again.",
      permissionRequiredTitle: "Permission required",
      permissionRequiredMessage: "Please allow access to your photo library to save photos.",
      openSettings: "Open Settings",
      cancel: "Cancel",
    },
    feedback: {
      additional_context: "Additional context needed",
      blurry_photos: "Please add less blurry photos",
      review_needed: "Review needed",
      photo_added: "{n} photo added",
      sections_need_update: "{n} section needs updated photos",
      request: "Request:",
      updated: "Updated",
    },
    qa: {
      schedule_dates: "Schedule start date and estimated completion date",
      homeowner_confirmed: "Homeowner confirmed",
      homeowner_approved_msg: "The homeowner has approved the scheduled start date",
      photo_submission_link: "Photo submission link",
      reschedule_reason: "Reschedule reason",
    },
    card: {
      completed: "Completed",
      add_photos: "Add photos",
      start: "Start",
      on_my_way: "On my way",
      checklists: "Checklists",
      address_not_mapped: "Address not mapped",
      on_site: "On site",
      drive_time_away: "{time} away",
      checklists_progress: "{submitted}/{total} Checklists",
    },
    file_management: {
      delete_error: "Failed to delete files. Please try again.",
      delete_confirm: "Delete {count} file(s)?",
      delete_confirm_msg: "This will move the selected files to trash.",
      cancel: "Cancel",
      delete: "Delete",
      selected: "{n} selected",
      actions: "Actions",
      download: "Download",
      move_to_trash: "Move to trash",
      delete_photo: "Delete photo",
      delete_photo_confirm: "Are you sure you want to move this photo to trash?",
      delete_anyway: "Delete anyway",
      warning: "Warning",
      counter_of: "{n} of {total}",
    },
    safety: {
      header: "Safety acknowledgement",
      clear: "Clear",
      sign_to_check_in: "Sign to check in",
      wrong_house_title: "You may be at the wrong house",
      wrong_house_body: "You're {distance} from {address}. Make sure you're at the right house before you check in.",
    },
  },
  settings: {
    title: "Settings",
    profile: {
      title: "Profile",
      description: "View your profile details",
      form: {
        name_label: "Name",
        organization_name_label: "Organization Name",
        phone_number_label: "Phone Number",
      },
      success_message: "Updated info",
      delete_account: "Delete account",
      refresh_profile: "Refresh profile",
    },
    preferences: {
      title: "Preferences",
      description: "Update notification and tracking preferences",
      notifications: "Notifications",
      location_services: "Location services",
      location_permission: {
        title: "Location access",
        status_granted: "Allowed",
        status_denied: "Not allowed",
        status_services_off: "Location is off for this phone",
        status_unknown: "Checking",
        explainer_title: "Drive times on your job list",
        explainer_body:
          "Remi can show how far away each job is. That distance is worked out on this phone, and the location it uses is not sent to Remi or shown to anyone else.",
        not_shared_title: "Not shared with the office",
        explainer_not_shared: "Allowing drive times does not share your location with the office.",
        allow: "Allow location access",
        services_off_body:
          "Location is turned off for this phone, so drive times are unavailable. You can turn it back on in your phone's settings.",
        settings_body: "Location access for Remi is set in your phone's settings.",
        open_settings: "Open settings",
      },
      save_images: "Save images to phone",
      on: "On",
      off: "Off",
    },
    security: {
      title: "Security",
      description: "Manage your login credentials",
    },
  },
  maps: {
    cancel: "Cancel",
    open_apple_maps: "Open Apple Maps",
    open_google_maps: "Open Google Maps",
    copy_address_to_clipboard: "Copy to clipboard",
  },
  notifications: {
    geofence_check_in: {
      with_homeowner:
        "Looks like you're on site — you've been checked in for {homeownerFirstName}'s roof work. Click to see project details.",
      without_homeowner: "Looks like you're on site — you've been checked in. Click to see project details.",
    },
    offline_sync: {
      pending_upload_singular: "1 item will upload when you're back online.",
      pending_upload_plural: "{count} items will upload when you're back online.",
    },
  },
  camera: {
    review_already_saved_accessibility_label: "Already saved to the checklist",
    review_undo_delete_accessibility_label: "Keep photo",
    review_delete_accessibility_label: "Delete photo",
    review_photos_accessibility_label: "Review captured photos",
    min_photos_delete_warning: "This question requires at least {count} photo(s).",
    focus_accessibility_label: "Tap to focus",
    not_ready: "Camera not ready",
    not_ready_msg: "Please wait a moment and try again.",
    permission_microphone: "We need microphone permission to record video.",
    unable_record_video: "Unable to record video",
    max_photos_reached: "Max photos reached",
    max_photos_msg: "You've already added the maximum number of photos for this question.",
    question_locked_title: "Question in use",
    question_locked_msg: "Another contractor is currently working on this question.",
    unable_take_photo: "Unable to take photo",
    try_again: "Please try again.",
    photo_captured_announcement: "Photo captured",
    take_photo_label: "Take photo",
    start_recording_label: "Start recording",
    stop_recording_label: "Stop recording",
    video_limit_reached_title: "Recording stopped",
    video_limit_reached_msg: "Videos can be up to {seconds} seconds long.",
    icloud_downloading: "Downloading from iCloud… {percent}%",
    permission_library: "We need photo library permission to pick an image.",
    loading: "Loading...",
    permission_required: "Camera permission required",
    allow_camera: "Allow camera",
    close: "Close",
    required: "Required",
    max: "Max",
    current: "Current",
    discard_photos_description_singular: "There is {n} photo that will not be added to the checklist requirement.",
    discard_photos_description_plural: "There are {n} photos that will not be added to the checklist requirement.",
    discard_photos: "Discard photos?",
    nevermind: "Nevermind",
    discard: "Discard",
    discarding: "Discarding...",
    upload_failed_title: "Upload failed",
    upload_failed_msg: "Some photos failed to upload. Please try again.",
    save_to_camera_roll_title: "Save to camera roll?",
    save_to_camera_roll_msg:
      "Photos are added to the checklist either way. You can change this later in Settings > Preferences.",
    save_to_camera_roll_decline: "Don't save",
    save_to_camera_roll_confirm: "Save",
    photo_mode: "PHOTO",
    video_mode: "VIDEO",
    done: "Done",
  },
  version_upgrade: {
    title: "Update Required",
    description: "A new version of the app is available. Please update to continue using the app.",
    update_button: "Update",
  },
} as const;
